/**
 * Platform billing — Phase 21 / Task 21.1 (PLATBILL-01, DEC-327).
 *
 * Paelode Systems billing its own hauler, operator and disposal customers. The six 21.1
 * tables are below; 21.2 adds the usage tables and 21.4 the invoice tables at the end:
 *
 *   1. `platform_billing_settings`        — one row per owner: legal name, address, logo,
 *                                           terms, prefixes, tax and the configurable
 *                                           sender (DEC-327 D8).
 *   2. `platform_billing_accounts`        — one per customer tenant: billing switch
 *                                           (D16), bill-to overrides, contacts, terms.
 *   3. `platform_rate_cards`              — a named price list for one tenant type.
 *   4. `platform_rate_card_versions`      — effective-dated versions; PUBLISHED is frozen.
 *   5. `platform_rate_card_lines`         — the priced lines of a version.
 *   6. `platform_customer_subscriptions`  — customer -> rate card + add-ons, dated, with
 *                                           no overlap per customer.
 *
 * Migration 0138 is authoritative for every constraint, trigger, policy and GRANT; this
 * file is the Drizzle view of it. The triggers (owner must be PLATFORM, customer must be
 * HAULER/OPERATOR/DISPOSAL, publish-lock, subscription overlap and tenant-type match)
 * and the composite foreign keys live in the migration only.
 *
 * RLS: every table keys on `owner_tenant_id` (always the Paelode PLATFORM tenant) with
 * USING + WITH CHECK, the invoices_isolation shape (0054). See 21-CONTEXT.md section 4.1.
 *
 * Money is integer cents; the API parses dollar strings with `parseDollarsToCents`.
 * The allowed meter / charge-kind / status values are `PLATFORM_BILLING_METERS` etc. in
 * `packages/shared/src/platform-billing.ts`.
 */

import {
  bigint,
  boolean,
  check,
  date,
  index,
  integer,
  jsonb,
  numeric,
  pgPolicy,
  pgTable,
  primaryKey,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';

const ownerIsolation = (name: string) =>
  pgPolicy(name, {
    as: 'permissive',
    to: appUserRole,
    for: 'all',
    using: sql`owner_tenant_id = current_setting('app.current_tenant_id', true)::uuid`,
    withCheck: sql`owner_tenant_id = current_setting('app.current_tenant_id', true)::uuid`,
  });

/** One element of `platform_rate_card_lines.bands` (a JSON array, validated by Zod). */
export interface PlatformRateCardBand {
  readonly fromQty: number;
  /** NULL = open-ended top band. */
  readonly toQty: number | null;
  readonly priceCents: number;
}

export const platformBillingSettings = pgTable(
  'platform_billing_settings',
  {
    ownerTenantId: uuid('owner_tenant_id')
      .primaryKey()
      .references(() => tenants.id),
    legalName: text('legal_name').notNull().default('Paelode Systems'),
    addressLine1: text('address_line1'),
    addressLine2: text('address_line2'),
    city: text('city'),
    state: text('state'),
    postalCode: text('postal_code'),
    logoDataUri: text('logo_data_uri'),
    remitInstructions: text('remit_instructions'),
    /**
     * OPEN-212 PR A (0268, DEC-387 D7): Paelode's own bank details, printed only on the invoices
     * of a customer whose account has showRemitBankDetails. The account number is stored SEALED
     * (services/platform-billing/remit-account.ts) and never returned; last4 is what screens show.
     */
    remitBankName: text('remit_bank_name'),
    remitRoutingNumber: text('remit_routing_number'),
    remitAccountSealed: text('remit_account_sealed'),
    remitAccountLast4: text('remit_account_last4'),
    remitEmail: text('remit_email'),
    footerText: text('footer_text'),
    defaultTermsDays: integer('default_terms_days').notNull().default(15),
    invoicePrefix: text('invoice_prefix').notNull().default('PS'),
    creditNotePrefix: text('credit_note_prefix').notNull().default('CN'),
    taxRateBps: integer('tax_rate_bps').notNull().default(0),
    fromAddress: text('from_address').notNull().default('billing@lodexpro.com'),
    fromDisplayName: text('from_display_name').notNull().default('Paelode Systems Billing'),
    replyTo: text('reply_to'),
    /**
     * Phase 21 / Task 21.2: the IANA zone that defines the local billing day. A trigger
     * (platform_billing_settings_timezone_guard) refuses any name not in
     * pg_timezone_names.
     */
    billingTimezone: text('billing_timezone').notNull().default('America/Chicago'),
    /**
     * Phase 21 / Task 21.4: the last invoice number issued (0 = none). 21.5 bumps it
     * atomically at SEND (DEC-327 D7), so a deleted draft leaves no gap.
     */
    invoiceSeq: bigint('invoice_seq', { mode: 'number' }).notNull().default(0),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    updatedByUserId: uuid('updated_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
  },
  () => [
    check('platform_billing_settings_terms_check', sql`default_terms_days BETWEEN 0 AND 365`),
    check('platform_billing_settings_invoice_prefix_check', sql`invoice_prefix ~ '^[A-Z]{1,6}$'`),
    check(
      'platform_billing_settings_credit_note_prefix_check',
      sql`credit_note_prefix ~ '^[A-Z]{1,6}$'`,
    ),
    check('platform_billing_settings_tax_check', sql`tax_rate_bps BETWEEN 0 AND 10000`),
    ownerIsolation('platform_billing_settings_isolation'),
  ],
);

export const platformBillingAccounts = pgTable(
  'platform_billing_accounts',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    customerTenantId: uuid('customer_tenant_id')
      .notNull()
      .references(() => tenants.id),
    billingStatus: text('billing_status').notNull().default('NOT_BILLED'),
    billToName: text('bill_to_name'),
    addressLine1: text('address_line1'),
    addressLine2: text('address_line2'),
    city: text('city'),
    state: text('state'),
    postalCode: text('postal_code'),
    toEmails: text('to_emails')
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    ccEmails: text('cc_emails')
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    termsDaysOverride: integer('terms_days_override'),
    poReference: varchar('po_reference', { length: 100 }),
    /** Free text, preserved as typed (DEC-147 notes exemption). */
    notes: text('notes'),
    /** OPEN-212 PR A (0268, DEC-387 D7): print Paelode's bank details on this customer's invoices. */
    showRemitBankDetails: boolean('show_remit_bank_details').notNull().default(false),
    /** OPEN-212 PR B (0272): this customer's Stripe Customer (cus_...), made on its first pay. */
    stripeCustomerId: text('stripe_customer_id'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    updatedByUserId: uuid('updated_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
  },
  (t) => [
    unique('platform_billing_accounts_customer_unique').on(t.ownerTenantId, t.customerTenantId),
    check(
      'platform_billing_accounts_status_check',
      sql`billing_status IN ('ACTIVE', 'NOT_BILLED', 'PAUSED')`,
    ),
    ownerIsolation('platform_billing_accounts_isolation'),
  ],
);

export const platformRateCards = pgTable(
  'platform_rate_cards',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    name: text('name').notNull(),
    tenantType: text('tenant_type').notNull(),
    description: text('description'),
    isArchived: boolean('is_archived').notNull().default(false),
    /** 0232: the hauler feature template the card sells (DEC-366 Q12); null when none. */
    featureTemplate: text('feature_template'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('platform_rate_cards_owner_id_unique').on(t.ownerTenantId, t.id),
    uniqueIndex('platform_rate_cards_owner_name_ci_idx').on(t.ownerTenantId, sql`lower(${t.name})`),
    check(
      'platform_rate_cards_tenant_type_check',
      sql`tenant_type IN ('HAULER', 'OPERATOR', 'DISPOSAL')`,
    ),
    ownerIsolation('platform_rate_cards_isolation'),
  ],
);

export const platformRateCardVersions = pgTable(
  'platform_rate_card_versions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    /** Composite FK (owner_tenant_id, rate_card_id) in migration 0138. */
    rateCardId: uuid('rate_card_id').notNull(),
    versionNumber: integer('version_number').notNull(),
    /** A local billing day (`YYYY-MM-DD`), not an instant. */
    effectiveFrom: date('effective_from').notNull(),
    status: text('status').notNull().default('DRAFT'),
    publishedAt: timestamp('published_at', { withTimezone: true }),
    publishedByUserId: uuid('published_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    createdByUserId: uuid('created_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    notes: text('notes'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('platform_rate_card_versions_owner_id_unique').on(t.ownerTenantId, t.id),
    unique('platform_rate_card_versions_number_unique').on(t.rateCardId, t.versionNumber),
    uniqueIndex('platform_rate_card_versions_published_day_idx')
      .on(t.rateCardId, t.effectiveFrom)
      .where(sql`status = 'PUBLISHED'`),
    check('platform_rate_card_versions_status_check', sql`status IN ('DRAFT', 'PUBLISHED')`),
    ownerIsolation('platform_rate_card_versions_isolation'),
  ],
);

export const platformRateCardLines = pgTable(
  'platform_rate_card_lines',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    /** Composite FK (owner_tenant_id, version_id) ON DELETE CASCADE in migration 0138. */
    versionId: uuid('version_id').notNull(),
    code: varchar('code', { length: 40 }).notNull(),
    description: text('description').notNull(),
    chargeKind: text('charge_kind').notNull(),
    meter: text('meter'),
    moduleCode: text('module_code'),
    unitPriceCents: integer('unit_price_cents').notNull(),
    /** numeric(14,4) — arrives as a STRING; never Number() it before money maths. */
    includedQuantity: numeric('included_quantity', { precision: 14, scale: 4 })
      .notNull()
      .default('0'),
    bands: jsonb('bands').$type<readonly PlatformRateCardBand[]>(),
    isAddOn: boolean('is_add_on').notNull().default(false),
    sortOrder: integer('sort_order').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('platform_rate_card_lines_code_unique').on(t.versionId, t.code),
    // Phase 21 / Task 21.4: target of platform_invoice_lines' composite FK.
    unique('platform_rate_card_lines_owner_id_unique').on(t.ownerTenantId, t.id),
    check('platform_rate_card_lines_code_check', sql`code ~ '^[A-Z0-9_]+$'`),
    check(
      'platform_rate_card_lines_charge_kind_check',
      sql`charge_kind IN ('FLAT_MONTHLY', 'PER_UNIT', 'TIERED_BAND', 'PER_MODULE')`,
    ),
    check('platform_rate_card_lines_price_check', sql`unit_price_cents >= 0`),
    check(
      'platform_rate_card_lines_bands_array_check',
      sql`bands IS NULL OR jsonb_typeof(bands) = 'array'`,
    ),
    ownerIsolation('platform_rate_card_lines_isolation'),
  ],
);

export const platformCustomerSubscriptions = pgTable(
  'platform_customer_subscriptions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    customerTenantId: uuid('customer_tenant_id')
      .notNull()
      .references(() => tenants.id),
    /** Composite FK (owner_tenant_id, rate_card_id) in migration 0138. */
    rateCardId: uuid('rate_card_id').notNull(),
    addOnCodes: text('add_on_codes')
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    startsOn: date('starts_on').notNull(),
    /** NULL = open-ended. */
    endsOn: date('ends_on'),
    notes: text('notes'),
    createdByUserId: uuid('created_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Phase 21 / Task 21.4: target of platform_invoices' composite FK.
    unique('platform_customer_subscriptions_owner_id_unique').on(t.ownerTenantId, t.id),
    index('platform_customer_subscriptions_customer_idx').on(
      t.ownerTenantId,
      t.customerTenantId,
      t.startsOn,
    ),
    check(
      'platform_customer_subscriptions_dates_check',
      sql`ends_on IS NULL OR ends_on >= starts_on`,
    ),
    ownerIsolation('platform_customer_subscriptions_isolation'),
  ],
);

export type PlatformBillingSettingsRow = typeof platformBillingSettings.$inferSelect;
export type PlatformBillingAccountRow = typeof platformBillingAccounts.$inferSelect;
export type PlatformRateCardRow = typeof platformRateCards.$inferSelect;
export type PlatformRateCardVersionRow = typeof platformRateCardVersions.$inferSelect;
export type PlatformRateCardLineRow = typeof platformRateCardLines.$inferSelect;
export type PlatformCustomerSubscriptionRow = typeof platformCustomerSubscriptions.$inferSelect;

// ── Phase 21 / Task 21.2 (PLATBILL-02): usage meters ────────────────────────────────

/**
 * One point-in-time count per (owner, customer, local billing day, meter), written by
 * the nightly snapshot sweeper (or a manual snapshot). The SECURITY DEFINER function
 * platform_usage_point_in_time() does the counting; the triggers (owner must be
 * PLATFORM, customer must be HAULER/OPERATOR/DISPOSAL) live in the migration only.
 */
export const platformUsageDaily = pgTable(
  'platform_usage_daily',
  {
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    customerTenantId: uuid('customer_tenant_id')
      .notNull()
      .references(() => tenants.id),
    /** A local billing day in platform_billing_settings.billing_timezone. */
    usageDate: date('usage_date').notNull(),
    meter: text('meter').notNull(),
    /** numeric(14,4) — arrives as a STRING; never Number() it before money maths. */
    value: numeric('value', { precision: 14, scale: 4 }).notNull(),
    /** e.g. `{ "modules": ["DAILY_EMAIL", "TICKET_VIEW"] }` for DISPOSAL_MODULES. */
    detail: jsonb('detail'),
    capturedAt: timestamp('captured_at', { withTimezone: true }).notNull().defaultNow(),
    source: text('source').notNull(),
  },
  (t) => [
    primaryKey({
      name: 'platform_usage_daily_pkey',
      columns: [t.ownerTenantId, t.customerTenantId, t.usageDate, t.meter],
    }),
    index('platform_usage_daily_date_idx').on(t.ownerTenantId, t.usageDate),
    check('platform_usage_daily_value_check', sql`value >= 0`),
    check('platform_usage_daily_source_check', sql`source IN ('SWEEPER', 'MANUAL')`),
    ownerIsolation('platform_usage_daily_isolation'),
  ],
);

/**
 * The claim log: one row per (owner, local billing day). Inserting it IS the decision
 * to snapshot that day, so a day is snapshotted once.
 */
export const platformUsageSnapshotRuns = pgTable(
  'platform_usage_snapshot_runs',
  {
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    usageDate: date('usage_date').notNull(),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull().defaultNow(),
    finishedAt: timestamp('finished_at', { withTimezone: true }),
    customers: integer('customers').notNull().default(0),
    rowsWritten: integer('rows_written').notNull().default(0),
    status: text('status').notNull().default('COMPLETE'),
    source: text('source').notNull(),
    triggeredByUserId: uuid('triggered_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
  },
  (t) => [
    primaryKey({
      name: 'platform_usage_snapshot_runs_pkey',
      columns: [t.ownerTenantId, t.usageDate],
    }),
    check('platform_usage_snapshot_runs_source_check', sql`source IN ('SWEEPER', 'MANUAL')`),
    ownerIsolation('platform_usage_snapshot_runs_isolation'),
  ],
);

export type PlatformUsageDailyRow = typeof platformUsageDaily.$inferSelect;
export type PlatformUsageSnapshotRunRow = typeof platformUsageSnapshotRuns.$inferSelect;

// ── Phase 21 / Task 21.4 (PLATBILL-03 part 1): invoices ─────────────────────────────

/** One entry of `platform_invoices.warnings` (shown before the draft is sent). */
export interface PlatformInvoiceWarning {
  readonly code: 'USAGE_PARTIAL' | 'UNKNOWN_ADD_ON' | 'PERIOD_NOT_ENDED';
  readonly message: string;
  readonly meter?: string;
  readonly daysCaptured?: number;
  readonly daysInPeriod?: number;
  readonly addOnCode?: string;
}

/** One entry of `platform_invoice_runs.skipped`. */
export interface PlatformInvoiceRunSkip {
  readonly customerTenantId: string;
  readonly customerName: string;
  readonly reason: string;
  readonly detail?: Record<string, unknown>;
}

/**
 * A Paelode invoice to one customer for one local calendar month. 21.4 writes DRAFT
 * only; the number, dates and void fields are 21.5's. The partial unique index
 * (one non-VOID invoice per owner, customer and period_start), the composite FKs and
 * the draft-only trigger live in the migration.
 */
export const platformInvoices = pgTable(
  'platform_invoices',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    customerTenantId: uuid('customer_tenant_id')
      .notNull()
      .references(() => tenants.id),
    /** Local billing days (`YYYY-MM-DD`) in the owner's billing_timezone. */
    periodStart: date('period_start').notNull(),
    periodEnd: date('period_end').notNull(),
    status: text('status').notNull().default('DRAFT'),
    /** NULL until SENT (DEC-327 D7). Unique per owner when set. */
    invoiceNumber: text('invoice_number'),
    issueDate: date('issue_date'),
    dueDate: date('due_date'),
    termsDays: integer('terms_days').notNull(),
    /** Composite FK (owner_tenant_id, rate_card_version_id): the version in force on period_end. */
    rateCardVersionId: uuid('rate_card_version_id').notNull(),
    /** Composite FK (owner_tenant_id, subscription_id): the subscription in force on period_end. */
    subscriptionId: uuid('subscription_id').notNull(),
    subtotalCents: integer('subtotal_cents').notNull(),
    taxRateBps: integer('tax_rate_bps').notNull().default(0),
    taxCents: integer('tax_cents').notNull().default(0),
    totalCents: integer('total_cents').notNull(),
    amountPaidCents: integer('amount_paid_cents').notNull().default(0),
    /** The usage roll-up the lines were priced from (an ARRAY). */
    usageSummary: jsonb('usage_summary').$type<readonly unknown[]>().notNull(),
    /** The account's bill-to fields at generation (an OBJECT). */
    billTo: jsonb('bill_to').$type<Record<string, unknown>>().notNull(),
    warnings: jsonb('warnings')
      .$type<readonly PlatformInvoiceWarning[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    /** The admin who generated it, or NULL for the scheduled run. */
    generatedBy: uuid('generated_by').references(() => users.id, { onDelete: 'set null' }),
    generationSource: text('generation_source').notNull(),
    voidAt: timestamp('void_at', { withTimezone: true }),
    voidedBy: uuid('voided_by').references(() => users.id, { onDelete: 'set null' }),
    voidReason: text('void_reason'),
    /** Free text, preserved as typed (DEC-147 notes exemption). */
    notes: text('notes'),
    pdfS3Key: text('pdf_s3_key'),
    // Phase 21 / Task 21.5 (DEC-327): the send, the stored PDF and the email outcome.
    sentAt: timestamp('sent_at', { withTimezone: true }),
    sentBy: uuid('sent_by').references(() => users.id, { onDelete: 'set null' }),
    /** PENDING / SENT / FAILED; NULL on a DRAFT. */
    emailStatus: text('email_status'),
    /** The SES MessageId of the last successful invoice email. */
    emailMessageId: text('email_message_id'),
    /** First line of the last email failure, capped. */
    emailError: text('email_error'),
    emailedAt: timestamp('emailed_at', { withTimezone: true }),
    /** 0232: the day it was paid (the latest live payment's date at PAID); cleared by a reversal. */
    paidOn: date('paid_on'),
    /** 0232: the last payment reminder sent. */
    lastRemindedAt: timestamp('last_reminded_at', { withTimezone: true }),
    /** Lowercase hex SHA-256 of the stored PDF, as the renderer reported it. */
    pdfSha256: text('pdf_sha256'),
    pdfSizeBytes: integer('pdf_size_bytes'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('platform_invoices_owner_id_unique').on(t.ownerTenantId, t.id),
    unique('platform_invoices_number_unique').on(t.ownerTenantId, t.invoiceNumber),
    uniqueIndex('platform_invoices_period_unique')
      .on(t.ownerTenantId, t.customerTenantId, t.periodStart)
      .where(sql`status <> 'VOID'`),
    index('platform_invoices_owner_period_idx').on(t.ownerTenantId, t.periodStart),
    check('platform_invoices_status_check', sql`status IN ('DRAFT', 'SENT', 'PAID', 'VOID')`),
    check(
      'platform_invoices_email_status_check',
      sql`email_status IS NULL OR email_status IN ('PENDING', 'SENT', 'FAILED')`,
    ),
    check(
      'platform_invoices_generation_source_check',
      sql`generation_source IN ('MANUAL', 'SCHEDULED')`,
    ),
    ownerIsolation('platform_invoices_isolation'),
  ],
);

/**
 * A line of a platform invoice. CHARGE lines are recomputable from their own row
 * (CHECK amount_cents = round(quantity * unit_price_cents)); ADJUSTMENT lines are a
 * signed one-off amount with a reason.
 */
export const platformInvoiceLines = pgTable(
  'platform_invoice_lines',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    /** Composite FK (owner_tenant_id, invoice_id) ON DELETE CASCADE in the migration. */
    invoiceId: uuid('invoice_id').notNull(),
    sortOrder: integer('sort_order').notNull().default(0),
    kind: text('kind').notNull(),
    /** Composite FK (owner_tenant_id, rate_card_line_id). NULL for an ADJUSTMENT. */
    rateCardLineId: uuid('rate_card_line_id'),
    chargeKind: text('charge_kind'),
    lineCode: varchar('line_code', { length: 40 }).notNull(),
    description: text('description').notNull(),
    meter: text('meter'),
    /** numeric — arrives as a STRING; never Number() it before money maths. */
    meterValue: numeric('meter_value'),
    moduleCode: text('module_code'),
    includedQuantity: numeric('included_quantity', { precision: 14, scale: 4 })
      .notNull()
      .default('0'),
    /** numeric(14,4) — a decimal STRING end to end. */
    quantity: numeric('quantity', { precision: 14, scale: 4 }).notNull(),
    unitPriceCents: integer('unit_price_cents').notNull(),
    amountCents: integer('amount_cents').notNull(),
    /** Required for an ADJUSTMENT; kept as typed. */
    reason: text('reason'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('platform_invoice_lines_invoice_idx').on(t.invoiceId, t.sortOrder),
    check('platform_invoice_lines_kind_check', sql`kind IN ('CHARGE', 'ADJUSTMENT')`),
    check(
      'platform_invoice_lines_amount_check',
      sql`kind <> 'CHARGE' OR amount_cents = round(quantity * unit_price_cents)`,
    ),
    ownerIsolation('platform_invoice_lines_isolation'),
  ],
);

/**
 * The monthly run's claim log: one row per (owner, period_start). Inserting it IS the
 * decision to run that month, so a month is generated once.
 */
export const platformInvoiceRuns = pgTable(
  'platform_invoice_runs',
  {
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    periodStart: date('period_start').notNull(),
    periodEnd: date('period_end').notNull(),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull().defaultNow(),
    finishedAt: timestamp('finished_at', { withTimezone: true }),
    customersConsidered: integer('customers_considered').notNull().default(0),
    draftsCreated: integer('drafts_created').notNull().default(0),
    skipped: jsonb('skipped')
      .$type<readonly PlatformInvoiceRunSkip[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    source: text('source').notNull(),
    triggeredByUserId: uuid('triggered_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
  },
  (t) => [
    primaryKey({
      name: 'platform_invoice_runs_pkey',
      columns: [t.ownerTenantId, t.periodStart],
    }),
    check('platform_invoice_runs_source_check', sql`source IN ('MANUAL', 'SCHEDULED')`),
    ownerIsolation('platform_invoice_runs_isolation'),
  ],
);

/**
 * Phase 21 / Task 21.6a (0232): the payments ledger. Append-only (a trigger refuses UPDATE and
 * DELETE; app_user holds SELECT and INSERT only). A REVERSAL names the PAYMENT it undoes.
 */
export const platformPayments = pgTable(
  'platform_payments',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    /** Composite FK (owner_tenant_id, invoice_id) in migration 0232. */
    invoiceId: uuid('invoice_id').notNull(),
    kind: text('kind').notNull(),
    amountCents: integer('amount_cents').notNull(),
    reversesPaymentId: uuid('reverses_payment_id'),
    paymentDate: date('payment_date').notNull(),
    method: text('method'),
    reference: text('reference'),
    notes: text('notes'),
    reason: text('reason'),
    recordedBy: uuid('recorded_by').references(() => users.id, { onDelete: 'set null' }),
    recordedAt: timestamp('recorded_at', { withTimezone: true }).notNull().defaultNow(),
    /**
     * OPEN-212 PR B (0272): 'STRIPE' and the Stripe object the money came from (a PaymentIntent
     * for a PAYMENT; a Dispute or Refund for a REVERSAL). NULL for a payment recorded by hand.
     * Unique per processor object and kind, so a replayed event cannot record money twice.
     */
    processor: text('processor'),
    processorRef: text('processor_ref'),
  },
  (t) => [
    unique('platform_payments_owner_id_unique').on(t.ownerTenantId, t.id),
    check('platform_payments_kind_check', sql`kind IN ('PAYMENT', 'REVERSAL')`),
    check('platform_payments_amount_positive', sql`amount_cents > 0`),
    ownerIsolation('platform_payments_isolation'),
  ],
);

/**
 * OPEN-212 PR B (0272, DEC-387): one Stripe try at paying one invoice. OPEN (a Checkout Session
 * exists) -> PENDING (submitted, settling) -> SUCCEEDED (one ledger PAYMENT row, payment_id) or
 * FAILED; OPEN -> CANCELED when the session expires or is replaced. At most one OPEN or PENDING
 * per invoice. Updated in place, never deleted.
 */
export const platformPaymentAttempts = pgTable(
  'platform_payment_attempts',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    ownerTenantId: uuid('owner_tenant_id')
      .notNull()
      .references(() => tenants.id),
    customerTenantId: uuid('customer_tenant_id')
      .notNull()
      .references(() => tenants.id),
    /** Composite FK (owner_tenant_id, invoice_id) in migration 0272. */
    invoiceId: uuid('invoice_id').notNull(),
    amountCents: integer('amount_cents').notNull(),
    status: text('status').notNull().default('OPEN'),
    source: text('source').notNull(),
    autopay: boolean('autopay').notNull().default(false),
    checkoutSessionId: text('checkout_session_id'),
    checkoutUrl: text('checkout_url'),
    checkoutExpiresAt: timestamp('checkout_expires_at', { withTimezone: true }),
    paymentIntentId: text('payment_intent_id'),
    /** Composite FK (owner_tenant_id, payment_id) -> platform_payments; set on SUCCEEDED. */
    paymentId: uuid('payment_id'),
    failureCode: text('failure_code'),
    failureMessage: text('failure_message'),
    createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    submittedAt: timestamp('submitted_at', { withTimezone: true }),
    settledAt: timestamp('settled_at', { withTimezone: true }),
    failedAt: timestamp('failed_at', { withTimezone: true }),
  },
  (t) => [
    unique('platform_payment_attempts_owner_id_unique').on(t.ownerTenantId, t.id),
    check(
      'platform_payment_attempts_status_check',
      sql`status IN ('OPEN', 'PENDING', 'SUCCEEDED', 'FAILED', 'CANCELED')`,
    ),
    check(
      'platform_payment_attempts_source_check',
      sql`source IN ('ADMIN', 'PAY_LINK', 'PORTAL', 'AUTOPAY')`,
    ),
    ownerIsolation('platform_payment_attempts_isolation'),
  ],
);

/** OPEN-212 PR B (0272): Stripe event ids already handled. Global, no RLS. */
export const stripeWebhookEvents = pgTable('stripe_webhook_events', {
  eventId: text('event_id').primaryKey(),
  type: text('type').notNull(),
  livemode: boolean('livemode').notNull(),
  stripeCreatedAt: timestamp('stripe_created_at', { withTimezone: true }).notNull(),
  receivedAt: timestamp('received_at', { withTimezone: true }).notNull().defaultNow(),
  /** APPLIED | IGNORED | UNMATCHED */
  outcome: text('outcome').notNull(),
});

export type PlatformPaymentAttemptRow = typeof platformPaymentAttempts.$inferSelect;
export type PlatformPaymentRow = typeof platformPayments.$inferSelect;
export type PlatformInvoiceRow = typeof platformInvoices.$inferSelect;
export type PlatformInvoiceLineRow = typeof platformInvoiceLines.$inferSelect;
export type PlatformInvoiceRunRow = typeof platformInvoiceRuns.$inferSelect;
